import {ProjectRoleRes, ProjectSummaryGroupingRes} from "../project.contract";
import {TrackerDetailRes} from "./tracker.contract";
import {ToDoGroupRes} from "../todo.contract";
import {Type} from 'class-transformer';
import {InvoiceDetailRes} from '../../cp/invoice.contract';
import {BaseTaskRes, EffortType} from '../task';
import {UsedTimeRes} from '../../resource-manager/used-time';
import moment from "moment";
import {
    ClassDisplayName,
    ColumnType,
    DetailGridIgnore,
    DetailGridProperties,
    DisplayName,
    FilterType,
    RequestOverride,
    TableColumn,
    TableDisplayFn,
    TableIgnore,
    TableIndex,
    yascColumnFilterType,
    yascFilterConfig,
    yascLevelOfDetail,
    YascOptionItem,
    yascRequired
} from '@yasc/yasc-material-components';
import {numberWithCommas} from '../../../../../apps/apps.component';
import {CompanyDetailsRes, CompanyMinimumRes} from '../../cp';
import {WorkflowDetailRes} from '../workflow.contract';

@ClassDisplayName('projectTableTitle')
export class ProjectTrackerRes extends TrackerDetailRes {
    @DisplayName('general_currentWorkflow')
    @DetailGridProperties({ row: 4, width: 2 })
    @TableIgnore(true)
    @TableColumn(ColumnType.Select)
    @RequestOverride('workflowId')
    @Type(() => WorkflowDetailRes)
    workflow!: WorkflowDetailRes;

    @DisplayName('projectTableVolume')
    @TableDisplayFn((value: number) => numberWithCommas(value))
    @TableIndex(10)
    @DetailGridProperties({ row: 5, width: 3 })
    @yascRequired()
    @yascLevelOfDetail(1)
    projectVolumeBrutto!: number;

    @DisplayName('projectTableRemainingAmount')
    @TableDisplayFn((value: number) => numberWithCommas(value))
    @TableIndex(11)
    @DetailGridProperties({ row: 5, width: 1 })
    @yascLevelOfDetail(1)
    projectVolumeBruttoOpen!: number;

    @DisplayName('projectTableOfferNr')
    @TableIndex(2)
    @DetailGridProperties({ row: 0, width: 2 })
    offerNr?: string;

    @DisplayName('projectTableOrderNr')
    @TableIndex(1)
    @DetailGridProperties({ row: 0, width: 2 })
    orderNr?: string;

    @TableIgnore(true)
    @DisplayName('general_description')
    @DetailGridProperties({ row: 2 })
    @yascLevelOfDetail(3)
    comment!: string;

    private _nextBill: any;

    @DisplayName('crm_projects_nextBill_tableHeader')
    @TableIndex(12)
    @TableColumn(ColumnType.Date)
    @yascLevelOfDetail(3)
    get nextBill(): any {
        return this._nextBill;
    }

    set nextBill(value: any) {
        this._nextBill = value ? moment(value) : null;
    }

    @Type(() => CompanyDetailsRes)
    @DisplayName('projectTableCustomer')
    @TableColumn(ColumnType.Select)
    @TableDisplayFn((value?: CompanyDetailsRes) => value?.name ?? '')
    @DetailGridProperties({ row: 3 })
    @RequestOverride('customerId')
    @TableIndex(0)
    @yascColumnFilterType(FilterType.autocomplete)
    customer!: CompanyDetailsRes | CompanyMinimumRes;

    @Type(() => InvoiceDetailRes)
    invoices: InvoiceDetailRes[] = [];

    @Type(() => ProjectRoleRes)
    teamMembers: ProjectRoleRes[] = [];

    @Type(() => ToDoGroupRes)
    toDos: ToDoGroupRes | null = null;

    @Type(() => BaseTaskRes)
    tasks: BaseTaskRes[] = [];

    @Type(() => UsedTimeRes)
    usedTime: UsedTimeRes[] = [];

    @Type(() => EffortType)
    allowedEffortTypes: EffortType[] = [];

    sleeper!: boolean;

    color?: string;

    toMinRes() {
        const minRes = new ProjectMinRes();
        minRes.id = this.id;
        minRes.name = this.name;
        minRes.type = this.type;
        minRes.isActive = this.isActive;
        minRes.currentWorkflowStatus = this.currentWorkflowStatus;
        minRes.statusHistory = this.statusHistory;
        minRes.notes = this.notes;

        minRes.offerNr = this.offerNr;
        minRes.orderNr = this.orderNr;
        minRes.comment = this.comment;
        minRes.teamMemberCount = this.teamMembers?.length ?? 0;
        minRes.sleeper = this.sleeper;
        minRes.color = this.color;
        minRes.nextBill = this.nextBill;
        minRes.projectVolumeBrutto = this.projectVolumeBrutto;
        minRes.projectVolumeBruttoOpen = this.projectVolumeBruttoOpen;

        if (this.customer) {
            const customer = new CompanyMinimumRes();
            customer.id = this.customer.id;
            customer.name = this.customer.name;
            minRes.customer = customer;
        }

        return minRes;
    }
}

@ClassDisplayName('projectTableTitle')
export class ProjectMinRes extends TrackerDetailRes {
    @Type(() => CompanyMinimumRes)
    @DisplayName('projectTableCustomer')
    @TableColumn(ColumnType.Select)
    @TableDisplayFn((value?: CompanyMinimumRes) => value?.name)
    @DetailGridProperties({ row: 0, width: 2 })
    @RequestOverride('customerId')
    @yascColumnFilterType(FilterType.autocomplete)
    @TableIndex(1)
    @yascFilterConfig({
        getGroup: (value?: ProjectSummaryGroupingRes) => value?.availableCompanies ?? [],
        mapToFilter: (value?: CompanyMinimumRes) =>
            value ? new YascOptionItem(value.name, value.id) : undefined as any
    })
    customer!: CompanyMinimumRes;

    @DisplayName('projectTableOrderNr')
    @TableIndex(2)
    @DetailGridProperties({ row: 0, width: 1 })
    orderNr?: string;

    @DisplayName('projectTableOfferNr')
    @TableIndex(3)
    @DetailGridProperties({ row: 0, width: 1 })
    offerNr?: string;

    @TableIgnore(true)
    @DisplayName('general_description')
    @DetailGridProperties({ row: 2, width: 4 })
    @yascLevelOfDetail(3)
    comment?: string;

    @DisplayName('projectTableTeamMembers')
    @TableIgnore(true)
    @DetailGridProperties({ row: 1, width: 1 })
    teamMemberCount!: number;

    private _nextBill: any;

    @DisplayName('crm_projects_nextBill_tableHeader')
    @TableIndex(4)
    @TableColumn(ColumnType.Date)
    @yascLevelOfDetail(2)
    get nextBill(): any {
        return this._nextBill;
    }

    set nextBill(value: any) {
        this._nextBill = value ? moment(value) : null;
    }

    @DisplayName('projectTableVolume')
    @TableDisplayFn((value: number) => numberWithCommas(value))
    @TableIndex(5)
    @DetailGridProperties({ row: 3, width: 2 })
    @yascLevelOfDetail(1)
    projectVolumeBrutto!: number;

    @DisplayName('projectTableRemainingAmount')
    @TableDisplayFn((value: number) => numberWithCommas(value))
    @TableIndex(6)
    @DetailGridProperties({ row: 3, width: 2 })
    @yascLevelOfDetail(1)
    projectVolumeBruttoOpen!: number;

    @TableIgnore(true)
    @DetailGridIgnore()
    sleeper!: boolean;

    @DisplayName('general_color')
    @DetailGridProperties({ row: 1, width: 1 })
    @yascLevelOfDetail(2)
    @TableIgnore(true)
    color?: string;
}